import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from "@mui/icons-material/Close";
import LockOutlineIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, Divider, InputAdornment, Stack, TextField, TextFieldProps, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ReactComponent as LinkIcon } from '../../../assets/icons/expand-link.svg';
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { UnassignedContentData } from '../../../contexts/InboxContext';
import { Dashboard, DocSendIngestion } from '../../../types/files';

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        height: 'fit-content',
        background: '#fefefe',
        "&:hover": {
            background: theme.colors.primary['50'],
        }
    },
    expanded: {
        background: theme.colors.primary['50'],
        borderLeft: `4px solid #0d9486`,
    },
    title: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#000',
    },
    addTo: {
        fontSize: '0.95rem',
        fontFamily: 'Lato',
        fontWeight: 'bold',
        color: theme.colors.neutral['800'],
    },
    chip: {
        height: 30,
        width: 120,
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 500,
        backgroundColor: theme.colors.orange['50'],
        color: theme.colors.orange['500'],
    },
    lastUpdatedDate: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
        whiteSpace: 'nowrap',
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            borderRadius: 8,
            padding: '0 8px !important',
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
    addAttachButton: {
      width: 'auto',
      minWidth: 100,
      height: 45,
      borderRadius: 8,
      color: theme.palette.primary.main,
      fontFamily: 'Inter',
      fontSize: '0.9rem',
      fontWeight: 500,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
    },
    discardButton: {
      width: 'auto',
      minWidth: 100,
      height: 45,
      borderRadius: 8,
      borderColor: theme.colors.neutral['400'],
      color: theme.colors.neutral['700'],
      fontFamily: 'Inter',
      fontSize: '0.9rem',
      fontWeight: 500,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
      '&:hover': {
        backgroundColor: theme.colors.neutral['100'],
        borderColor: theme.colors.neutral['600'],
      }
    },
    accessButton: {
      width: '45%',
      minWidth: 120,
      height: 50,
      borderRadius: 8,
      backgroundColor: '#0d9486',
      color: 'white',
      fontFamily: 'Lato',
      fontSize: '0.9rem',
      fontWeight: 600,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
    },
    dismissButton: {
      width: '45%',
      minWidth: 120,
      height: 50,
      borderRadius: 8,
      borderColor: theme.colors.neutral['400'],
      color: theme.colors.neutral['700'],
      fontFamily: 'Lato',
      fontSize: '0.9rem',
      fontWeight: 600,
      textTransform: 'none',
      transition: 'ease-in-out 300ms',
      '&:hover': {
        backgroundColor: theme.colors.neutral['100'],
        borderColor: theme.colors.neutral['600'],
      }
    },
    notificationBlock: {
        width: '100%',
        height: 150,
        transition: 'height .3s ease',
    },
    notificationIcon: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        fill: theme.palette.primary.main,
    },
    notificationText: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
    },
    ownership: {
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#666666',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        wordBreak: 'break-all',
    },
    owner: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#7e7e7e',
    },
    preview :{
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        color: '#aeaeae',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        wordBreak: 'break-all',
    },
    passworded: {
        width: 'fit-content',
        height: 'fit-content',
        padding: '6px 12px',
        borderRadius: 12,
        background: '#fbf5ff',
    },
    icon: {
        width: 24,
        height: 24,
        padding: 'unset',
    },
    lockedContent: {
        marginTop: 4,
        padding: 8,
        paddingBottom: 4,
        borderRadius: 12,
        background: '#f4e8ff',
    },
    link: {
        display: 'block',
        width: '100%',
        height: 'fit-content',
        padding: '6px 16px',
        border: `1px solid #eff6ff`,
        borderRadius: 8,
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 500,
        textDecoration: 'none',
        color: theme.colors.neutral['700'],
        backgroundColor: theme.colors.blue['50'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        "&:hover": {
            textDecoration: 'none !important',
        },
    },
}));

const UnassignedDocSendContent: React.FC<{
    contentData: UnassignedContentData,
    docsend: DocSendIngestion,
    createdAt?: string,
    onDelete: () => void,
    onIngestion: (docsend: DocSendIngestion, password: string) => Promise<any>,
}> = ({ contentData, docsend, createdAt, onDelete, onIngestion }) => {
    const classes = useStyles();
    const { dashboards } = useContext(DashboardsContext);
    const [dashboard, setDashboard] = useState<Dashboard|undefined>(undefined);
    const inputValue = useRef<TextFieldProps>(null);
    const [isNotificationVisible, setIsNotificationVisible] = useState<boolean>(false);
    const [notification, setNotification] = useState<string|null>(null);

    const { content } = contentData;

    const sanitizeUrl = (originUrl: string) => originUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');

    const handleAssign = useCallback(() => {
        setNotification(`DocSend successfully attached to ${dashboard?.title || 'dashboard'}`);
        setIsNotificationVisible(true);
    }, [dashboard]);

    useEffect(() => {
        setDashboard(dashboards.find(dashboard => docsend?.existingDashboardId ?
            dashboard.id === docsend?.existingDashboardId : dashboard.title === docsend?.newDashboardTitle));
    }, [docsend, dashboards]);

    useEffect(() => {
        if (isNotificationVisible) {
            const timeoutId: any = setTimeout(() => {
                clearTimeout(timeoutId);
                onIngestion(docsend, inputValue.current?.value as string).then(() => {
                    setIsNotificationVisible(false);
                    setNotification(null);
                });
            }, 3000);

            return () => {
                clearTimeout(timeoutId);
            }
        }
        // eslint-disable-next-line
    }, [isNotificationVisible]);

    if (!dashboard)
        return (<></>);

    if (isNotificationVisible) {
        return (<>
            <Stack spacing={2} className={classes.notificationBlock} alignItems="center" justifyContent="center">
                <CheckCircleRoundedIcon className={classes.notificationIcon}/>
                <Typography className={classes.notificationText}> {notification} </Typography>
            </Stack>
        </>);
    }

    return (<>
        <Stack className={classes.container}>
            <Divider sx={{ width: '100%' }}/>
            <Box height={8} />
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start" p={2} width="100%">
                <Box className={classes.lockedContent}>
                    <LockOutlineIcon sx={{ width: 24, height: 24, color: '#9037eb' }} />
                </Box>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" pl="4px" width="100%">
                    <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="flex-start">
                        <Typography component="span" className={classes.title} display="inline">
                            {content?.subject?.replace('Fwd:', '')?.replace('Fw:', '') || '<Untitled>'}
                        </Typography>
                        <Typography className={classes.ownership}>
                            {'From: '}
                            <Typography component="span" className={classes.owner} display="inline" pl="4px">
                                {!!content.from ? content.from.replace(/<.*?>/g, '').trim() : 'N/A'}
                            </Typography>
                        </Typography>
                        <Typography className={classes.ownership}>
                            {'Source: '}
                            <Typography component="span" className={classes.owner} display="inline" pl="4px"
                                sx={{ textTransform: `${!!content.dealSource ? 'capitalize' : 'lowercase'} !important`}}>
                                {content.dealSource?.name || content.source || 'N/A'}
                            </Typography>
                        </Typography>
                        <Box height={8} />
                        <Stack direction="row" className={classes.passworded} spacing={1} alignItems="center" justifyContent="flex-start">
                            <LockOutlineIcon sx={{ width: 20, height: 20, color: '#9037eb' }} />
                            <Typography fontFamily="Inter" fontSize="0.9rem" fontWeight={500} color="#9037eb">
                                {`Password needed`}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" spacing="4px" alignItems="center" justifyContent="flex-end">
                        {!!createdAt && (
                            <Typography className={classes.lastUpdatedDate}>{`${moment(createdAt).fromNow()}`}</Typography>
                        )}
                    </Stack>
                </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pl={3} pr={4} pb={2}>
                <Typography
                    component="a"
                    className={classes.link}
                    fontWeight={`bold !important`}
                    color={`#1d4ed8 !important`}
                    href={docsend?.url || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ cursor: 'pointer !important' }}>
                    <LinkIcon style={{ position: 'relative', bottom: -3, right: 6, width: 20, height: 18, stroke: '#1d4ed8' }} />
                    {sanitizeUrl(docsend?.url || '')}
                </Typography>
            </Stack>
            <Stack width="100%" pl={3} pr={4}>
                <TextField variant="outlined"
                    className={classes.inputField}
                    placeholder={'Enter password here ...'}
                    inputRef={inputValue}
                    sx={{ width: '100%' }}
                    InputProps={{ startAdornment: (
                        <InputAdornment position="start">
                            <LockOutlineIcon sx={{ width: 24, height: 24, color: '#adb1be' }} />
                        </InputAdornment>
                    )}} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" pl={3} pr={4} pb={2} pt={2}>
                <Button variant="contained"
                    className={classes.accessButton}
                    onClick={handleAssign}>
                    {'Access Document'}
                </Button>
                <Button variant="outlined"
                    className={classes.dismissButton}
                    startIcon={<CloseIcon />}
                    onClick={onDelete}>
                    {'Dismiss Email'}
                </Button>
            </Stack>
        </Stack>
    </>);
}

export default UnassignedDocSendContent;
